/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
// redux
import {useDispatch, useSelector} from 'react-redux';
import { deleteUser } from '../../../../../../state/thunks/users';
// material
import { SubPage, Copy, BodyCopy, CTAs, Button, Group, Radio, SingleSelectDropDown, InlineMessage, displayModal } from '@insticator/insticator-ui'; // iui-material
// styles
import injectSheet from 'react-jss';
import styles from './deleteuser_styles';

/* ========== ~~~~~~~~~~ ACCOUNT EDIT USER ~~~~~~~~~~ ========== */
const DeleteUser = props => {
  const dispatch = useDispatch();
  const { deletingUserStatus, accountUsers } = useSelector(state => state.account)
  const { user: { userUUID, firstName, lastName, email, commentingUser }, userHasCommenting } = props;
  const [selectedDeleteOpt, selectDeleteOpt] = useState('delete-comments')
  const [selectedUser, selectUser] = useState(null)
  const [deleteUserSubmitted, submitDeleteUser] = useState(false)

  const handleCancel = () => dispatch(displayModal(false));

  const handleDelete = () => {
    submitDeleteUser(true)
    if(selectedDeleteOpt === 'transfer-comments') {
      if(selectedUser) deleteUserRequest()
    } else {
      deleteUserRequest()
    }
  }

  const deleteUserRequest = useCallback(async () => {
    const response = await dispatch(deleteUser({userUUID, transferredUserUUID: selectedUser?.value || ''}));
    dispatch(displayModal(false));
  }, [selectedUser]);

  const haveSameSites = (sites1, sites2) => {
    if (sites1.length === sites2.length) {
      return sites1.every(element => {
        if (sites2.includes(element)) return true;
        return false;
      });
    }
    return false;
  }

  const users = useMemo(() => accountUsers
    .filter(user => user.userUUID !== userUUID)
    .filter(user => !!user.commentingUser)
    .map(user => ({name: `${user.firstName} ${user.lastName} (${user.email}) - ${user.commentingUser.roleName}`, value: user.userUUID, sites: user.commentingUser.allowedSiteUUIDs}))
    ,[accountUsers])

  const showTransferCommentsToUserError = selectedDeleteOpt === 'transfer-comments' && !selectedUser && deleteUserSubmitted 
  
  return (
      <SubPage name={`deleteuser`} klass={props.classes.deleteuser}>
          <Copy name={`deleteuser`}>
              <BodyCopy>{`Are you sure you want to delete the user ${firstName} ${lastName} (${email})?`}</BodyCopy>
              { userHasCommenting && 
                <Group>
                  <Radio
                    color={'primary'} 
                    isList={true}  
                    title={'Delete this user along with all of their historical comments.'}
                    checked={selectedDeleteOpt === 'delete-comments'}
                    handleChange={() => selectDeleteOpt('delete-comments')}
                  />
                  { users.length > 0 &&
                  <>
                  <Radio 
                    color={'primary'} 
                    isList={true} 
                    title={`Delete this user but transfer all historical comments to another selected user. 
                    By switching users, the historical comments will reflect the username and role of the new user. 
                    Select the user from below`}
                    checked={selectedDeleteOpt === 'transfer-comments'}
                    handleChange={() => selectDeleteOpt('transfer-comments')}
                  />
                  { selectedUser && !haveSameSites(selectedUser.sites, commentingUser.allowedSiteUUIDs) &&
                      <InlineMessage
                        name={'sitespermissionmsg'}
                        variant={'neutral'}
                        icon={'exclamation-triangle'}
                        message={'User will transfer the selected user permissions to comment on the same sites as the user being deleted. The transferred user will have to log out and re-login in order for these new site permissions to take effect.'}
                        close={false}
                      />
                  }
                  { selectedDeleteOpt === 'transfer-comments' &&
                    <SingleSelectDropDown 
                      klass={`usersdropdown error-${showTransferCommentsToUserError}`}
                      name={selectedUser?.name || 'Users'} 
                      selected={selectedUser}
                      onSelect={user => selectUser(user)} 
                      items={users}
                      searchable={true} 
                      paginated={false} 
                    />
                  }
                  { showTransferCommentsToUserError && <div className={'errormessage'}>Select a user to transfer comments to</div>}
                  </>
                  }
                </Group>
              }
          </Copy>
          <CTAs name={`ctas`} >
              <Button type={`action`} handleClick={handleCancel} variant={`normal`} name={`cancel`} text={`Cancel`} />
              <Button type={'action'} handleClick={handleDelete} variant={`delete`} name={`delete`} text={`Delete`} operation={'delete'} processing={deletingUserStatus === 'pending'} />
          </CTAs>
      </SubPage>
  );
};

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(DeleteUser);
